import { type FC, type ReactNode, createContext, useCallback, useState } from 'react';
import { type ProcessedAnalysisConfig } from '@/components/Studies/Groups/Analysis/Analysis.util';
import { StudyApiId } from '@/model/Study.model.ts';
import { _noop, _notNil } from '@/littledash.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import {
  AnalysisConfigDefaultData,
  AnalysisConfigResponse,
  AnalysisIssue,
  AnalysisOptions,
  AnalysisTableDataType,
  AnalysisTableResponse,
  AnalysisType,
  ExportSnapshotApiId,
  StatColumnV1,
} from '@/model/Analysis.model.ts';

interface AnalysisContextValue {
  options: AnalysisOptions | undefined;
  config: Map<AnalysisType, ProcessedAnalysisConfig> | undefined;
  issues: Array<AnalysisIssue> | undefined;
  exportSnapshotApiId: ExportSnapshotApiId | undefined;
  tableData: { type: AnalysisType; columns: Array<StatColumnV1>; rows: AnalysisTableResponse['rows'] } | undefined;
  tableLoading: boolean;
  tableLoadingError: boolean;
  updateAnalysisOptions: (options: AnalysisOptions, studyApiId: StudyApiId) => Promise<void>;
  updateAnalysisConfig: (config: AnalysisConfigResponse) => void;
  updateAnalysisIssues: (issues: Array<AnalysisIssue>) => void;
  updateAnalysisTable: (tableResponse: AnalysisTableResponse, studyApiId: StudyApiId) => void;
  updateAnalysisDefault: (options: AnalysisConfigDefaultData, tableResponse: AnalysisTableResponse) => void;
}

export const AnalysisContext = createContext<AnalysisContextValue>({
  options: undefined,
  config: undefined,
  issues: undefined,
  exportSnapshotApiId: undefined,
  tableData: undefined,
  tableLoading: false,
  tableLoadingError: false,
  updateAnalysisOptions: async () => {},
  updateAnalysisConfig: _noop,
  updateAnalysisIssues: _noop,
  updateAnalysisTable: _noop,
  updateAnalysisDefault: _noop,
});

interface AnalysisProviderProps {
  children: ReactNode;
}

export const AnalysisProvider: FC<AnalysisProviderProps> = ({ children }) => {
  const [options, setOptions] = useState<AnalysisOptions | undefined>(undefined);
  const [config, setConfig] = useState<Map<AnalysisType, ProcessedAnalysisConfig> | undefined>(undefined);
  const [issues, setIssues] = useState<Array<AnalysisIssue> | undefined>(undefined);
  const [exportSnapshotApiId, setExportSnapshotApiId] = useState<ExportSnapshotApiId | undefined>(undefined);
  const [tableData, setTableData] = useState<AnalysisTableDataType | undefined>(undefined);
  const [tableLoadingError, setTableLoadingError] = useState<boolean>(false);

  const { invoke, loading: tableLoading } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/analysis/generate',
    invokeOnInit: false,
  });

  const updateAnalysisConfig = useCallback((configResponse: AnalysisConfigResponse) => {
    const processedConfig = new Map(
      configResponse.config.map(({ type, measurements, average_type, tracking_from }) => [
        type,
        {
          measurements: new Set(measurements),
          average_type: new Set(average_type),
          tracking_from: new Set(tracking_from),
        },
      ])
    );
    setConfig(processedConfig);
    setIssues(configResponse.issues ?? []);
  }, []);

  const updateAnalysisIssues = useCallback((newIssues: Array<AnalysisIssue>) => {
    setIssues(newIssues);
  }, []);

  const updateAnalysisTable = useCallback((tableResponse: AnalysisTableResponse) => {
    setTableData({ type: tableResponse.type, columns: tableResponse.columns, rows: tableResponse.rows });
  }, []);

  const updateAnalysisOptions = useCallback(
    async (updatedOptions: AnalysisOptions, studyApiId: StudyApiId) => {
      setOptions(updatedOptions);
      if (_notNil(studyApiId) && _notNil(updatedOptions)) {
        const response = await invoke({
          path: { studyApiId },
          body: updatedOptions,
        });
        if (_notNil(response.body)) {
          if (response.type === 'success') {
            setTableLoadingError(false);
            updateAnalysisTable(response.body);
            setExportSnapshotApiId(response.body?.export_snapshot_api_id);
          } else {
            setTableLoadingError(true);
          }
        }
      }
    },
    [invoke, updateAnalysisTable]
  );

  const updateAnalysisDefault = useCallback(
    (defaultConfig: AnalysisConfigDefaultData, defaultTable: AnalysisTableResponse) => {
      if (_notNil(defaultConfig)) {
        const analysisOptions: AnalysisOptions = {
          type: defaultConfig.type,
          measurement: defaultConfig.measurement ?? '',
          average_type: defaultConfig.average_type,
          day: defaultConfig.day ?? 0,
          day_from: defaultConfig.day_from,
          target_value: defaultConfig.target_value,
          set_as_default: false,
        };
        setOptions(analysisOptions);
      }
      if (_notNil(defaultTable)) {
        updateAnalysisTable(defaultTable);
        setExportSnapshotApiId(defaultTable?.export_snapshot_api_id);
      }
    },
    [updateAnalysisTable]
  );

  const contextValue: AnalysisContextValue = {
    options,
    config,
    issues,
    exportSnapshotApiId,
    tableData,
    tableLoading,
    tableLoadingError,
    updateAnalysisOptions,
    updateAnalysisConfig,
    updateAnalysisIssues,
    updateAnalysisTable,
    updateAnalysisDefault,
  };

  return <AnalysisContext.Provider value={contextValue}>{children}</AnalysisContext.Provider>;
};
